import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, PieChart, Pie, Cell, BarChart, Bar, LabelList,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import {
  ShoppingCart, IndianRupee, TriangleAlert, Package, Siren, TrendingUp, FileCheck, Download,
} from 'lucide-react';
import { request, api } from '../../api/client';
import { usePermission } from '../../store/auth';
import { brand } from '../../assets/brand';
import { Select, Dropdown, Button, useApp, Card, Tag, Alert, Tooltip, DateRangePicker, Skeleton, cn } from '../../ui';
import { CountUp, Stagger } from '../../ui/motion';

const inr = (n: any) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n ?? 0));

// Compact axis labels in Indian units (₹ lakhs / crores) — fits the data scale.
const compact = (v: any): string => {
  const n = Number(v);
  const a = Math.abs(n);
  if (a >= 1_00_00_000) return `${(n / 1_00_00_000).toFixed(1)}Cr`;
  if (a >= 1_00_000) return `${(n / 1_00_000).toFixed(1)}L`;
  if (a >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
};

// Comprehensive business report (daily/monthly/yearly) downloaded as a PDF from the
// server. Gated to DASH.EXECUTIVE (the report endpoint enforces it too).
function DownloadReportButton() {
  const { message } = useApp();
  const [busy, setBusy] = useState(false);
  const download = async (period: string, from: string, to: string) => {
    setBusy(true);
    try {
      const res = await api.get('/reports/summary.pdf', { responseType: 'blob', params: { from, to, period } });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `GBB-report-${from}_${to}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not generate the report.');
    } finally {
      setBusy(false);
    }
  };
  const onClick = ({ key }: { key: string }) => {
    const today = dayjs().format('YYYY-MM-DD');
    if (key === 'today') download('daily', today, today);
    else if (key === 'month') download('monthly', dayjs().startOf('month').format('YYYY-MM-DD'), today);
    else if (key === 'year') download('yearly', dayjs().startOf('year').format('YYYY-MM-DD'), today);
  };
  return (
    <Dropdown
      disabled={busy}
      menu={{ onClick, items: [
        { key: 'today', label: 'Daily — today' },
        { key: 'month', label: 'Monthly — this month' },
        { key: 'year', label: 'Yearly — this year' },
      ] }}
    >
      <Button variant="primary" loading={busy} icon={<Download size={16} />}>Download Report (PDF)</Button>
    </Dropdown>
  );
}

const PRESETS: { label: string; value: [Dayjs, Dayjs] }[] = [
  { label: 'Today', value: [dayjs(), dayjs()] },
  { label: 'Last 7 days', value: [dayjs().subtract(7, 'day'), dayjs()] },
  { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), dayjs()] },
  { label: 'This month', value: [dayjs().startOf('month'), dayjs()] },
  { label: 'Last quarter', value: [dayjs().subtract(90, 'day'), dayjs()] },
];

// A small status-mix palette (pipeline health).
const STATUS_COLOR: Record<string, string> = {
  DRAFT: '#bfbfbf', PLACED: '#1677FF', APPROVED: '#13c2c2', PACKED: '#722ed1',
  DISPATCHED: '#2f54eb', DELIVERED: '#339022', INVOICED: '#52c41a', CLOSED: '#8c8c8c', CANCELLED: '#D9363E',
};

// Themed tooltip — uses surface tokens so it adapts to light/dark automatically.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function MoneyTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-pop">
      <div className="font-semibold text-content">{label}</div>
      <div className="text-muted">{inr(payload[0].value)}</div>
    </div>
  );
}

// Reusable horizontal-bar chart (top SKUs / leaderboard). `onItem(index)` navigates.
function HBar({ data, color, onItem, height }: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[]; color: string; onItem: (i: number) => void; height: number;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart layout="vertical" data={data} margin={{ top: 4, right: 72, left: 4, bottom: 4 }}>
        <CartesianGrid horizontal={false} strokeDasharray="3 3" />
        <XAxis type="number" tickFormatter={compact} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
        <YAxis
          type="category" dataKey="name" width={124} tickLine={false} axisLine={false} tick={{ fontSize: 12 }}
          tickFormatter={(v: string) => (v && v.length > 16 ? v.slice(0, 15) + '…' : v)}
        />
        <RTooltip content={<MoneyTooltip />} cursor={{ fill: 'var(--gb-surface-2)' }} />
        <Bar
          dataKey="value" fill={color} radius={[0, 4, 4, 0]} barSize={16} cursor="pointer"
          onClick={(_: unknown, i: number) => onItem(i)}
        >
          <LabelList dataKey="value" position="right" formatter={(v: any) => inr(v)} style={{ fontSize: 11 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function ExecSalesDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(30, 'day'), dayjs()]);
  const [territoryId, setTerritoryId] = useState<string | undefined>();
  const [salesPersonId, setSalesPersonId] = useState<string | undefined>();

  // Permission-aware: a SALES_PERSON has DASH.SALES but not DASH.EXECUTIVE/OUTSTANDING.VIEW.
  const canExec = usePermission('DASH.EXECUTIVE');
  const canOutstanding = usePermission('OUTSTANDING.VIEW');
  const canSales = usePermission('DASH.SALES');

  const from = range[0].format('YYYY-MM-DD');
  const to = range[1].format('YYYY-MM-DD');
  const params = { from, to, salesPersonId, territoryId };

  const execQ = useQuery({
    queryKey: ['dashboard-executive'],
    queryFn: () => request<any>({ url: '/dashboard/executive' }),
    enabled: canExec,
  });
  const agingQ = useQuery({
    queryKey: ['dashboard-aging'],
    queryFn: () => request<any[]>({ url: '/outstanding/aging' }),
    enabled: canOutstanding,
  });
  const summaryQ = useQuery({
    queryKey: ['dashboard-sales-summary', params],
    queryFn: () => request<any>({ url: '/dashboard/sales/summary', params }),
    enabled: canSales,
  });
  const topQ = useQuery({
    queryKey: ['dashboard-top-skus', from, to, salesPersonId, territoryId],
    queryFn: () => request<any[]>({ url: '/dashboard/products/top', params: { from, to, salesPersonId, territoryId, limit: 10 } }),
    enabled: canSales,
  });
  const territoriesQ = useQuery({
    queryKey: ['territories-list'],
    queryFn: () => request<any[]>({ url: '/territories' }),
    enabled: canSales,
  });
  const usersQ = useQuery({
    queryKey: ['sales-users'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_PERSON', pageSize: 200 } }),
    enabled: canSales,
  });

  const exec = execQ.data ?? {};
  const summary = summaryQ.data ?? {};

  // Roll the per-retailer aging rows up to portfolio totals.
  const aging = useMemo(() => {
    return (agingQ.data ?? []).reduce(
      (a: any, r: any) => ({
        b0_30: a.b0_30 + Number(r.b0_30 || 0),
        b31_60: a.b31_60 + Number(r.b31_60 || 0),
        b61_90: a.b61_90 + Number(r.b61_90 || 0),
        b_gt90: a.b_gt90 + Number(r.b_gt90 || 0),
        total: a.total + Number(r.total || 0),
      }),
      { b0_30: 0, b31_60: 0, b61_90: 0, b_gt90: 0, total: 0 },
    );
  }, [agingQ.data]);
  const overdue30 = aging.b31_60 + aging.b61_90 + aging.b_gt90;

  // Revenue trend backfilled to zero for every day in range (no x-axis holes).
  const trend = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of summary.trend ?? []) map.set(String(t.day).slice(0, 10), Number(t.total || 0));
    const out: { day: string; total: number }[] = [];
    let cur = range[0].startOf('day');
    const end = range[1].startOf('day');
    let guard = 0;
    while (!cur.isAfter(end) && guard < 400) {
      const k = cur.format('YYYY-MM-DD');
      out.push({ day: k, total: map.get(k) ?? 0 });
      cur = cur.add(1, 'day');
      guard++;
    }
    return out;
  }, [summary.trend, range]);

  const byPerson = summary.byPerson ?? [];
  const byStatus = summary.byStatus ?? [];
  const topSkus = topQ.data ?? [];

  // ---- recharts data ----
  const statusData = byStatus.map((s: any) => ({ name: s.status, value: Number(s.count) }));
  const totalOrders = statusData.reduce((s: number, x: any) => s + x.value, 0);
  const topData = topSkus.map((p: any) => ({ name: p.sku, value: Number(p.revenue) }));
  const personData = byPerson.map((p: any) => ({ name: p.fullName, value: Number(p.total) }));
  const agingData = [
    { name: '0–30', value: aging.b0_30, fill: '#339022' },
    { name: '31–60', value: aging.b31_60, fill: '#E7B22C' },
    { name: '61–90', value: aging.b61_90, fill: '#FA8C16' },
    { name: '>90', value: aging.b_gt90, fill: '#D9363E' },
  ];

  const go = (url: string) => navigate(url);

  // Donut tooltip (needs totalOrders for the % — closes over it here).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const StatusTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0];
    const pct = totalOrders ? Math.round((p.value / totalOrders) * 100) : 0;
    return (
      <div className="rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-pop">
        <div className="font-semibold text-content">{p.name}</div>
        <div className="text-muted">{p.value} orders · {pct}%</div>
      </div>
    );
  };

  // Friendly placeholder so sparse/empty data never renders a broken-looking chart.
  const EmptyChart = ({ height, text }: { height: number; text: string }) => (
    <div className="flex items-center justify-center text-sm text-subtle" style={{ height }}>{text}</div>
  );

  // badge: 'MTD' = month-to-date figure; 'Live' = current point-in-time snapshot.
  const Kpi = ({ title, value, format, color, sub, icon, badge, accent = brand.primary, onClick, loading }: {
    title: ReactNode; value: any; format?: 'inr'; color?: string; sub?: ReactNode; icon?: ReactNode;
    badge?: 'MTD' | 'Live'; accent?: string; onClick?: () => void; loading?: boolean;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-full min-h-[92px] w-full items-center gap-3 rounded-card border border-line bg-surface px-4 py-4 text-left shadow-soft transition duration-150 hover:-translate-y-0.5 hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div
        className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-lg transition-transform duration-150 group-hover:scale-105 [&>svg]:size-5"
        style={{ background: `${accent}1A`, color: accent }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center justify-between gap-2">
          <span className="truncate text-xs text-muted">{title}</span>
          {badge && (
            <Tooltip label={badge === 'Live' ? 'Current snapshot (live)' : 'This month to date'}>
              <Tag tone={badge === 'Live' ? 'green' : 'neutral'} className="text-2xs">{badge}</Tag>
            </Tooltip>
          )}
        </div>
        {/* Value renders in text-content (legible) by default — the accent stays on the
            border + icon only. `color` still forces a semantic emphasis (e.g. danger red). */}
        <div className={cn('text-2xl font-bold leading-tight tracking-tight', !color && 'text-content')} style={color ? { color } : undefined}>
          {loading ? (
            <Skeleton className="h-6 w-24" />
          ) : typeof value === 'number' ? (
            <CountUp value={value} format={format === 'inr' ? inr : (n) => String(Math.round(n))} />
          ) : format === 'inr' ? inr(value) : value}
        </div>
        {sub && <div className="mt-0.5 text-2xs">{sub}</div>}
      </div>
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-card px-6 py-6 text-white shadow-card"
        style={{
          background: `radial-gradient(130% 150% at 88% -20%, ${brand.primary} 0%, ${brand.primaryDark} 52%, #0f3d1f 100%)`,
          borderLeft: `4px solid ${brand.harvest}`,
        }}
      >
        {/* subtle scientific dot-grid + a soft light bloom for depth */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:radial-gradient(#fff_1px,transparent_1px)] [background-size:18px_18px]" />
        <div className="pointer-events-none absolute -right-16 -top-20 size-64 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[1.5px] text-white/85">
            <span className="size-1.5 rounded-full bg-harvest-300 ring-4 ring-harvest-300/25" />
            Executive &amp; Sales
          </div>
          <div className="mt-2 text-2xl font-bold">How Green Bay Bio is performing</div>
          <div className="mt-1 text-xs text-white/85">
            {brand.tagline}
            {exec.refreshedAt ? ` · snapshot refreshed ${dayjs(exec.refreshedAt).format('hh:mm A')}` : ''}
          </div>
        </div>
      </div>

      {canExec && (
        <div className="flex justify-end">
          <DownloadReportButton />
        </div>
      )}

      {(execQ.isError || summaryQ.isError || agingQ.isError) && (
        <Alert
          type="error"
          icon={<TriangleAlert />}
          title="Some dashboard data couldn't be loaded"
          description="Figures below may be incomplete — please retry shortly."
        />
      )}

      {/* Zone A — finance MTD figures + live snapshots (not range-driven) */}
      {(canExec || canOutstanding) && (
        <Stagger className="flex flex-wrap gap-4 [&>*]:min-w-[210px] [&>*]:flex-1" stagger={0.06} y={14}>
          {canExec && (
            <Kpi title="Revenue (MTD)" badge="MTD" accent="#339022" value={exec.salesMtd ?? 0} format="inr" loading={execQ.isLoading}
              icon={<ShoppingCart />} onClick={() => go('/orders')} />
          )}
          {canExec && (
            <Kpi title="Collections (MTD)" badge="MTD" accent="#1677FF" value={exec.collectionMtd ?? 0} format="inr" loading={execQ.isLoading}
              icon={<IndianRupee />} onClick={() => go('/billing/invoices')} />
          )}
          {canOutstanding && (
            <Kpi title="Outstanding" badge="Live" accent="#E7B22C" value={aging.total} format="inr" loading={agingQ.isLoading}
              icon={<FileCheck />} onClick={() => go('/billing/aging')} />
          )}
          {canOutstanding && (
            <Kpi title="Overdue >30d" badge="Live" accent="#D9363E" value={overdue30} format="inr" color="#D9363E" loading={agingQ.isLoading}
              icon={<TriangleAlert />} onClick={() => go('/billing/aging')} />
          )}
          {canExec && (
            <Kpi title="Inventory Value" badge="Live" accent="#13C2C2" value={exec.inventoryValue ?? 0} format="inr" loading={execQ.isLoading}
              icon={<Package />} onClick={() => go('/inventory/stock')} />
          )}
          {canExec && (
            <Kpi title="Active Complaints" badge="Live" accent="#722ED1" value={exec.complaintsOpen ?? 0} loading={execQ.isLoading}
              icon={<Siren />} onClick={() => go('/complaints')}
              color={Number(exec.complaintsSlaBreach) > 0 ? '#D9363E' : undefined}
              sub={Number(exec.complaintsSlaBreach) > 0
                ? <span className="text-danger-500">{exec.complaintsSlaBreach} breaching SLA</span>
                : <span className="text-subtle">SLA on track</span>} />
          )}
        </Stagger>
      )}

      {/* Zone B — selected period (range-driven) */}
      {canSales && (
        <div className="space-y-4">
          {/* Period header + filters on one row (filters drive this range-based zone) */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted">For selected period · {from} → {to}</div>
            <div className="flex flex-wrap items-center gap-2">
              <DateRangePicker value={range} onChange={setRange} presets={PRESETS} />
              <Select
                placeholder="All territories" allowClear showSearch optionFilterProp="label" style={{ width: 200 }}
                value={territoryId} onChange={setTerritoryId}
                options={(territoriesQ.data ?? []).map((t: any) => ({ value: String(t.id), label: `${t.code} · ${t.name}` }))}
              />
              <Select
                placeholder="All sales persons" allowClear showSearch optionFilterProp="label" style={{ width: 220 }}
                value={salesPersonId} onChange={setSalesPersonId}
                options={(usersQ.data?.data ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName }))}
              />
            </div>
          </div>

          <Stagger className="grid grid-cols-2 gap-4 md:grid-cols-4" stagger={0.06} y={14}>
            <Kpi title="Period Revenue" accent="#339022" value={summary.salesTotal ?? 0} format="inr" loading={summaryQ.isLoading}
              icon={<TrendingUp />} onClick={() => go(`/orders${salesPersonId ? `?salesPersonId=${salesPersonId}` : ''}`)} />
            <Kpi title="Period Orders" accent="#2F54EB" value={summary.orderCount ?? 0} loading={summaryQ.isLoading}
              icon={<ShoppingCart />} onClick={() => go(`/orders${salesPersonId ? `?salesPersonId=${salesPersonId}` : ''}`)}
              sub={<span className="text-muted">AOV {inr(summary.aov ?? 0)} · {summary.openOrders ?? 0} open</span>} />
          </Stagger>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card className="md:col-span-2" title="Revenue trend" extra="Click a day → orders">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart
                  data={trend} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}
                  onClick={(s: any) => s?.activeLabel && go(`/orders?from=${s.activeLabel}&to=${s.activeLabel}`)}
                >
                  <defs>
                    <linearGradient id="gbTrendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={brand.primary} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={brand.primary} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={(v: string) => String(v).slice(5)} tickLine={false} axisLine={false} minTickGap={24} tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={compact} tickLine={false} axisLine={false} width={48} tick={{ fontSize: 11 }} />
                  <RTooltip content={<MoneyTooltip />} cursor={{ stroke: 'var(--gb-border-strong)' }} />
                  <Area type="monotone" dataKey="total" stroke={brand.primary} strokeWidth={2} fill="url(#gbTrendFill)" dot={false} activeDot={{ r: 4 }} cursor="pointer" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card title="Order status mix" extra="Click → orders">
              {byStatus.length ? (
                <div>
                  <div className="relative" style={{ height: 196 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusData} dataKey="value" nameKey="name" innerRadius={56} outerRadius={80}
                          paddingAngle={2} stroke="var(--gb-surface)" strokeWidth={3}
                          onClick={(d: any) => d?.name && go(`/orders?status=${d.name}`)}
                        >
                          {statusData.map((s: any) => <Cell key={s.name} fill={STATUS_COLOR[s.name] ?? '#999'} cursor="pointer" />)}
                        </Pie>
                        <RTooltip content={<StatusTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-2xl font-bold leading-none text-content">{totalOrders}</div>
                      <div className="mt-0.5 text-2xs text-subtle">orders</div>
                    </div>
                  </div>
                  <div className="mt-1 flex flex-wrap justify-center gap-x-3 gap-y-1">
                    {statusData.map((s: any) => (
                      <span key={s.name} className="inline-flex items-center gap-1 text-2xs text-muted">
                        <span className="size-2 rounded-full" style={{ background: STATUS_COLOR[s.name] ?? '#999' }} />
                        {s.name}
                      </span>
                    ))}
                  </div>
                </div>
              ) : <EmptyChart height={220} text="No orders in this period" />}
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Card title="Top 10 SKUs by revenue" extra="Click → product">
              {topSkus.length ? (
                <HBar data={topData} color={brand.harvest} height={240}
                  onItem={(i) => topSkus[i] && go(`/products?sku=${encodeURIComponent(topSkus[i].sku)}`)} />
              ) : <EmptyChart height={240} text="No invoiced product sales in this period" />}
            </Card>
            <Card title="Sales-person leaderboard" extra="Click → their orders">
              {byPerson.length ? (
                <HBar data={personData} color={brand.primary} height={240}
                  onItem={(i) => byPerson[i] && go(`/orders?salesPersonId=${byPerson[i].salesPersonId}`)} />
              ) : <EmptyChart height={240} text="No invoiced sales by anyone yet" />}
            </Card>
          </div>
        </div>
      )}

      {/* Receivables aging (snapshot) */}
      {canOutstanding && (
        <Card
          title={<span className="inline-flex items-center gap-2">Receivables aging <Tag tone="neutral">Snapshot</Tag></span>}
          extra="Click → outstanding aging"
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={agingData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }} onClick={() => go('/billing/aging')}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={compact} tickLine={false} axisLine={false} width={48} tick={{ fontSize: 11 }} />
              <RTooltip content={<MoneyTooltip />} cursor={{ fill: 'var(--gb-surface-2)' }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={52} cursor="pointer">
                {agingData.map((d) => <Cell key={d.name} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
